import './RouteWaypointInput.scss'

import { IconCategory } from '@here/hds-components'
import { InputInputPayload } from '@here/hds-input'
import { HDSIcon } from '@here/hds-react-components'
import { WeGoInput } from 'lib/custom-elements/wego-input/wego-input-react'
import { i18n } from 'lib/i18n'
import React, { useCallback, useMemo, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'

import { WaypointInput, WaypointType } from './RouteInputs'

export interface RouteWaypointInputProps {
  waypointInput: WaypointInput
  waypointType: WaypointType
  onEnterKeyDown?: (e: CustomEvent | KeyboardEvent) => void
  onFocus?: (waypointInput: WaypointInput) => void
  onChange?: (waypointInput: WaypointInput) => void
  onBlur?: () => void
  inputColor?: string
  isDraggable: boolean
  showDragIcon?: number
  children?: JSX.Element
  isFocused?: boolean
}

export const RouteWaypointInput = ({
  isFocused,
  waypointInput,
  waypointType,
  onEnterKeyDown,
  onFocus,
  onChange,
  onBlur,
  inputColor,
  isDraggable,
  showDragIcon,
  children,
}: RouteWaypointInputProps): JSX.Element => {
  const [isDragHovered, setIsDragHovered] = useState(false)
  const isSource = waypointType === WaypointType.source
  const isStop = waypointType === WaypointType.stop
  const isDestination = waypointType === WaypointType.destination
  const id = useMemo(
    () => (waypointInput?.id !== undefined ? waypointInput?.id : crypto.randomUUID()),
    [waypointInput]
  )

  const i18nBaseKey = 'route-planner.'
  const inputPlaceHolder = isSource
    ? i18n(`${i18nBaseKey}set-starting-point-placeholder`)
    : isDestination
    ? i18n(`${i18nBaseKey}destination-input-placeholder`)
    : i18n(`${i18nBaseKey}set-stop-input-placeholder`)

  const iconName =
    showDragIcon === waypointInput.index || isDragHovered
      ? 'drag-list'
      : isSource
      ? 'current-location'
      : isDestination
      ? 'destination'
      : 'point'

  const iconCategory: IconCategory =
    showDragIcon === waypointInput.index || isDragHovered
      ? 'core-ui'
      : isSource
      ? 'map-view'
      : isDestination
      ? 'travel-transport-tracking'
      : 'poi'

  const iconStyle =
    inputColor !== undefined
      ? ({
          '--hds-icon-color': inputColor || 'var(--hds-foreground-secondary)',
        } as React.CSSProperties)
      : ({} as React.CSSProperties)

  const inputStyle =
    inputColor !== undefined
      ? ({
          '--hds-foreground-primary': inputColor || '--hds-foreground-primary',
        } as React.CSSProperties)
      : {}

  const handleFocus = useCallback(() => {
    onFocus && onFocus(waypointInput)
  }, [onFocus, waypointInput])

  const handleInput = useCallback(
    (event: CustomEvent<InputInputPayload>) => {
      const value = event.detail.value
      onChange &&
        onChange({
          ...waypointInput,
          wayPoint: { ...waypointInput.wayPoint, place: value as string },
        })
    },
    [onChange, waypointInput]
  )

  return (
    <Draggable isDragDisabled={isDraggable} draggableId={id} key={id} index={waypointInput.index}>
      {(provided) => (
        <div ref={provided.innerRef} className="RouteInputs__row" {...provided.draggableProps}>
          <div
            onMouseDown={onBlur}
            onMouseOver={() => !isDraggable && setIsDragHovered(true)}
            onMouseOut={() => !isDraggable && setIsDragHovered(false)}
            className="RouteInputs__icon-container"
            {...provided.dragHandleProps}
          >
            <HDSIcon
              name={iconName}
              category={iconCategory as IconCategory}
              style={iconStyle}
              iconStyle="outline"
            />
          </div>
          <div className="RouteInputs__input-container">
            <WeGoInput
              disabled={isDraggable && isStop ? true : undefined}
              autoFocus={isFocused}
              placeholder={inputPlaceHolder}
              size="medium"
              type="text"
              value={waypointInput.wayPoint?.place ?? ''}
              style={inputStyle}
              onKeyDown={onEnterKeyDown}
              onFocus={handleFocus}
              onInput={handleInput}
              onBlur={onBlur}
              selectOnFocus={true}
            >
              {children}
            </WeGoInput>
          </div>
        </div>
      )}
    </Draggable>
  )
}
